import { Text } from "@/components/ui/text";
import { Rank } from "@components/Rank";
import { Avatar } from "@features/Avatar";
import dayjs from "dayjs";
import { Show } from "solid-js";
import { Dynamic } from "solid-js/web";
import { css } from "styled-system/css";
import { Box, Flex, Grid } from "styled-system/jsx";
import Chip from "../../Setups/Chip";
import { PROVIDER_META } from "../providerMeta";
import type { VideoProvider } from "../detectVideoUrl";

const externalUrl = (provider: VideoProvider, providerId: string) => {
  if (provider === 'youtube') return `https://www.youtube.com/watch?v=${providerId}`;
  if (provider === 'dailymotion') return `https://www.dailymotion.com/video/${providerId}`;
  return `https://vimeo.com/${providerId}`;
};

export default function Poster(props: {
  username?: string | null;
  id?: string | null;
  rank?: string | null;
  date?: string | null;
  topicLabel: string;
  topicColor: string;
  provider: VideoProvider;
  providerId: string;
}) {
  const meta = () => PROVIDER_META[props.provider];

  return (
    <Show when={props.username && props.id}>
      <Flex
        alignItems='center'
        gap='13px'
        flexWrap='wrap'
        pb='18px'
        borderBottomWidth='1px'
        borderBottomStyle='solid'
        borderBottomColor='var(--colors-editorial-hair)'
      >
        <Box position='relative' flexShrink={0} w='44px' h='44px'>
          <Avatar id={props.id ?? undefined} />
          <Show when={props.rank}>
            <Box
              position='absolute'
              right='-3px'
              bottom='-3px'
              borderRadius='full'
              bg='bg.emphasized'
              boxShadow='0 0 0 1px var(--colors-editorial-hair-strong)'
            >
              <Rank rank={props.rank ?? undefined} dimensions='16px' />
            </Box>
          </Show>
        </Box>
        <Grid gap='2px' minWidth={0}>
          <Text fontSize='14.5px' fontWeight='700' color='editorialInk' textTransform='capitalize'>
            {props.username}
          </Text>
          <Text fontSize='12px' color='editorialInk3'>
            {dayjs(props.date).format('MMMM D, YYYY')}
          </Text>
        </Grid>

        <Chip color={props.topicColor}>{props.topicLabel}</Chip>

        <Box flex='1' />

        <a
          href={externalUrl(props.provider, props.providerId)}
          target="_blank"
          rel="noopener noreferrer"
          class={css({
            display: 'inline-flex',
            alignItems: 'center',
            gap: '7px',
            h: '36px',
            px: '14px',
            borderRadius: '9px',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'var(--colors-editorial-hair-strong)',
            color: 'editorialInk',
            bg: 'editorialPanel',
            fontWeight: '600',
            fontSize: '12.5px',
            textDecoration: 'none',
            _hover: {
              borderColor: 'color-mix(in srgb, var(--colors-editorial-accent) 50%, transparent)',
              color: 'editorialAccentDeep',
            },
          })}
        >
          <Dynamic component={meta().icon} size={14} />
          Open on {meta().label}
        </a>
      </Flex>
    </Show>
  )
}
